import React, { useState } from 'react';
import { FiFileText, FiLock, FiCheck, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { sanitizePayload } from "../../lib/sanitize";
import { supabase } from '../../lib/supabaseClient';
import { getEdgeWorkerUrl } from '../../lib/edgeWorkerUrl';

export default function RCADocumentBlock({ rcaRecord, onFinalized, ticketId, severity }) {
  const [notes, setNotes] = useState(rcaRecord?.content || rcaRecord?.payload?.notes || '');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const isDraft = rcaRecord?.status?.toLowerCase() !== 'finalized' && rcaRecord?.status?.toLowerCase() !== 'locked';
  const hasRecord = !!rcaRecord && Object.keys(rcaRecord).length > 0;

  const abortControllerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
       if (abortControllerRef.current) {
           abortControllerRef.current.abort();
       }
    };
  }, [ticketId]);

  const handleGenerateRCA = async () => {
    if (abortControllerRef.current) {
        abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsGenerating(true);
    const toastId = toast.loading("Generating RCA...");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Active session token required.");

      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/tickets/${ticketId}/generate-rca`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        signal: abortControllerRef.current.signal
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to generate RCA.');

      toast.success("RCA Post-Mortem generated successfully.", {
        id: toastId,
        style: { background: '#09090b', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }
      });
      if (onFinalized) onFinalized();
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error(`RCA Generation Error: ${err.message}`, { id: toastId });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFinalize = async () => {
    if (!notes.trim()) {
      toast.error('Investigation notes are required before finalizing.');
      return;
    }

    setIsFinalizing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error("Active session token required.");

      const workerUrl = getEdgeWorkerUrl();
      // Use existing endpoint for finalizing RCA
      let res;
      if (rcaRecord.payload) {
          res = await fetch(`${workerUrl}/api/v1/actions/rca/finalize`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ rcaLogId: rcaRecord.id, notes })
          });
      } else {
         // Direct update if using the new table
         const { error } = await supabase.from('rca_documents').update({ status: 'FINALIZED', content: notes }).eq('id', rcaRecord.id);
         if (error) throw error;
         res = { ok: true, json: async () => ({ success: true }) };
      }

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to finalize RCA.');

      toast.success("RCA document successfully finalized and locked.", {
        style: { background: '#09090b', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }
      });

      if (onFinalized) onFinalized();
    } catch (err) {
      toast.error(`RCA Finalization Error: ${err.message}`);
    } finally {
      setIsFinalizing(false);
    }
  };

  if (!hasRecord) {
    if (severity === 'urgent' || severity === 'high' || severity === 'CRITICAL' || severity === 'HIGH') {
        return (
          <div className="p-4 rounded-2xl bg-indigo-950/20 border border-indigo-500/30 space-y-4 shadow-lg shadow-indigo-900/10 flex flex-col items-start">
             <div className="flex items-center gap-2 text-indigo-400 mb-2">
                <FiFileText className="text-lg" />
                <h3 className="text-xs font-mono font-bold uppercase tracking-widest">Post-Mortem Required</h3>
              </div>
             <button
                onClick={handleGenerateRCA}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-mono font-bold uppercase transition-all disabled:opacity-50 disabled:hover:bg-indigo-600 shadow-md shadow-indigo-900/50"
              >
                {isGenerating ? 'Generating...' : <><FiFileText /> Generate RCA Post-Mortem with Onyx</>}
              </button>
          </div>
        );
    }
    return null;
  }

  return (
    <div className="p-4 rounded-2xl bg-indigo-950/20 border border-indigo-500/30 space-y-4 shadow-lg shadow-indigo-900/10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-indigo-400">
          <FiFileText className="text-lg" />
          <h3 className="text-xs font-mono font-bold uppercase tracking-widest">Root Cause Analysis</h3>
        </div>
        <div>
          {isDraft ? (
            <span className="px-2 py-1 rounded bg-amber-500/20 text-amber-400 text-[10px] font-black uppercase tracking-wider border border-amber-500/30">
              Draft
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-wider border border-emerald-500/30">
              <FiLock className="text-[10px]" /> Finalized
            </span>
          )}

          <button
            onClick={handleGenerateRCA}
            disabled={isGenerating}
            className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-500/10 text-indigo-400 text-[10px] font-bold uppercase tracking-wider border border-indigo-500/20 hover:bg-indigo-500/20 transition-colors disabled:opacity-50"
            title="Regenerate RCA"
          >
            <FiRefreshCw className={isGenerating ? "animate-spin" : ""} /> {isGenerating ? "Regenerating" : "Regenerate"}
          </button>
        </div>
      </div>

            <div className="space-y-2">
         <div className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">Breach Type</div>
         <div className="text-sm font-semibold text-zinc-200">{rcaRecord?.payload?.breach_type ?? 'System Incident'}</div>
      </div>

      <div className="space-y-2 pt-2 border-t border-indigo-500/20">
         <div className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">Corrective Actions</div>
         <div className="text-sm font-semibold text-zinc-200 whitespace-pre-wrap">{rcaRecord?.payload?.corrective_actions ?? 'None specified.'}</div>
      </div>

      <div className="space-y-2 pt-2 border-t border-indigo-500/20">
         <div className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">Incident Timeline</div>
         <div className="text-sm font-semibold text-zinc-200 whitespace-pre-wrap">{rcaRecord?.payload?.incident_timeline ?? 'No timeline available.'}</div>
      </div>

      <div className="space-y-2 pt-2 border-t border-indigo-500/20">
        <label className="text-[11px] font-mono text-indigo-300 uppercase tracking-widest font-bold">
          Human-in-the-Loop Investigation Notes
        </label>
        {isDraft ? (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isFinalizing}
            placeholder="Document your findings, root cause verification, and long-term resolution here..."
            className="w-full p-3 rounded-xl bg-black/50 border border-indigo-500/30 text-xs text-zinc-200 placeholder-indigo-500/40 focus:outline-none focus:border-indigo-400/60 transition-all resize-none font-sans min-h-[250px]"
          />
        ) : (
          <div className="w-full p-4 rounded-xl bg-black/30 border border-indigo-500/20 text-xs text-zinc-300 font-sans min-h-[80px] prose prose-sm prose-invert prose-indigo max-w-none prose-p:leading-relaxed prose-pre:bg-black/50 prose-pre:border prose-pre:border-indigo-500/30">
            <div dangerouslySetInnerHTML={{ __html: sanitizePayload({ html: notes || 'No notes provided.' }).html }} />
          </div>
        )}
      </div>

      {isDraft && (
        <div className="flex justify-end pt-2">
          <button
            onClick={handleFinalize}
            disabled={isFinalizing || !notes.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-mono font-bold uppercase transition-all disabled:opacity-50 disabled:hover:bg-indigo-600 shadow-md shadow-indigo-900/50"
          >
            {isFinalizing ? 'Locking...' : <><FiCheck /> Save & Notify Leadership</>}
          </button>
        </div>
      )}
    </div>
  );
}
